import React, { useEffect, useRef } from 'react';
import { usePitchMonitor } from './usePitchMonitor';
import { GRADE_STYLES, SCOOP_COLOR, SCOOP_UP, SCOOP_DOWN } from './gradeOverlay';
import { pitchClassName } from '../../utils/pitchGrading';

/**
 * Pitch monitor (desktop, Record group): sing or hum and see, live, what
 * the grader hears. A scrolling graph of the pitch readings, marking which
 * count as singing; the note and cents being sung right now; the mic level;
 * and each held note judged exactly as a graded take would judge it,
 * against a chosen target note or the nearest note.
 */

const WINDOW_SECONDS = 8;
const HALF_RANGE = 8; // semitones either side of the centre
const MINUS = '−';
const TARGETS = Array.from({ length: 49 }, (_, i) => 36 + i); // C2..C6
const CLEAR = 0.6;
// What the "browser voice processing" note on the status line can and cannot tell
const PROCESSING_TIP = 'Whether the browser itself is processing the mic (it is asked not to). '
  + 'The sound card or Windows can still process it out of sight (Audio enhancements, Voice clarity, '
  + 'the laptop maker\'s audio app), and another app on a call can make Windows turn other sounds down '
  + '(Sound control panel, Communications tab: "Do nothing" stops that).';

const noteName = (midi) => `${pitchClassName(midi)}${Math.floor(Math.round(midi) / 12) - 1}`;
const signed = (cents) => (cents > 0 ? `+${cents}` : cents < 0 ? `${MINUS}${-cents}` : '0');
const centsFrom = (midi, base) => {
  const c = (midi - base) * 100;
  return Math.round(c - 1200 * Math.round(c / 1200));
};

function describeNote(n) {
  const name = noteName(n.target);
  let verdict;
  if (n.nearest) verdict = `held ${signed(n.cents)}¢ from ${name}`;
  else if (n.grade === 'good') verdict = `${name} · in tune (${signed(n.cents)}¢)`;
  else if (n.grade === 'close') verdict = `${name} · ${Math.abs(n.cents)}¢ ${n.cents > 0 ? 'sharp' : 'flat'}`;
  else verdict = `${name} · sang ${pitchClassName(n.level)} (${signed(n.cents)}¢)`;
  const scoop = n.scoop
    ? ` · ${n.scoop.direction === 'up' ? 'scooped up from' : 'slid down from'} ${n.scoop.cents}¢ ${n.scoop.direction === 'up' ? 'below' : 'above'}`
    : '';
  return verdict + scoop;
}

function useGraph(canvasRef, open, bufferRef, notesRef, target) {
  const centreRef = useRef(57);
  const targetRef = useRef(target);
  targetRef.current = target;

  useEffect(() => {
    if (!open) return undefined;
    let frame = 0;
    const draw = () => {
      frame = requestAnimationFrame(draw);
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ratio = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (canvas.width !== Math.round(width * ratio) || canvas.height !== Math.round(height * ratio)) {
        canvas.width = Math.round(width * ratio);
        canvas.height = Math.round(height * ratio);
      }
      const g = canvas.getContext('2d');
      g.setTransform(ratio, 0, 0, ratio, 0, 0);
      g.clearRect(0, 0, width, height);

      const buffer = bufferRef.current;
      const now = buffer.length ? buffer[buffer.length - 1].time : 0;
      const from = now - WINDOW_SECONDS;
      // Follow the target, or the voice: re-centre when singing leaves the view
      const goal = targetRef.current;
      if (goal !== null) {
        centreRef.current = goal;
      } else {
        const recent = buffer.filter(b => b.sung && b.time > now - 1.5).map(b => b.midi).sort((a, b) => a - b);
        if (recent.length) {
          const held = recent[recent.length >> 1];
          if (Math.abs(held - centreRef.current) > HALF_RANGE - 2) centreRef.current = Math.round(held);
        }
      }
      const top = centreRef.current + HALF_RANGE;
      const bottom = centreRef.current - HALF_RANGE;
      const left = 40;
      const x = (t) => left + ((t - from) / WINDOW_SECONDS) * (width - left - 8);
      const y = (m) => 8 + ((top - m) / (top - bottom)) * (height - 16);

      // Note lines, C's stronger, every note named
      g.font = '10px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
      g.textBaseline = 'middle';
      for (let m = Math.ceil(bottom); m <= Math.floor(top); m++) {
        g.strokeStyle = m % 12 === 0 ? '#cbd5e1' : '#eef2f7';
        g.lineWidth = 1;
        g.beginPath();
        g.moveTo(left, y(m));
        g.lineTo(width - 8, y(m));
        g.stroke();
        g.fillStyle = m % 12 === 0 ? '#475569' : '#94a3b8';
        g.fillText(noteName(m), 4, y(m));
      }
      // The target, with the in-tune band
      if (goal !== null) {
        g.fillStyle = 'rgba(22, 163, 74, 0.14)';
        g.fillRect(left, y(goal + 0.25), width - left - 8, y(goal - 0.25) - y(goal + 0.25));
        g.strokeStyle = '#334155';
        g.setLineDash([5, 4]);
        g.beginPath();
        g.moveTo(left, y(goal));
        g.lineTo(width - 8, y(goal));
        g.stroke();
        g.setLineDash([]);
      }
      // Readings: counted as singing (blue) or heard but not counted (grey)
      for (const b of buffer) {
        if (b.time < from || b.midi < bottom || b.midi > top) continue;
        if (b.sung) {
          g.fillStyle = '#2563eb';
          g.beginPath();
          g.arc(x(b.time), y(b.midi), 2, 0, Math.PI * 2);
          g.fill();
        } else if (b.clarity >= 0.3) {
          g.fillStyle = 'rgba(148, 163, 184, 0.7)';
          g.beginPath();
          g.arc(x(b.time), y(b.midi), 1.4, 0, Math.PI * 2);
          g.fill();
        }
      }
      // Judged notes: the graded stretch as a bar, its verdict above
      for (const n of notesRef.current) {
        if (n.to < from) continue;
        const colour = GRADE_STYLES[n.grade].color;
        g.strokeStyle = colour;
        g.lineWidth = 4;
        g.globalAlpha = 0.55;
        g.beginPath();
        g.moveTo(x(Math.max(from, n.gradedFrom)), y(n.level));
        g.lineTo(x(n.gradedTo), y(n.level));
        g.stroke();
        g.globalAlpha = 1;
        g.fillStyle = colour;
        g.font = 'bold 11px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
        const label = `${signed(n.cents)}¢${n.scoop ? ` ${n.scoop.direction === 'up' ? SCOOP_UP : SCOOP_DOWN}` : ''}`;
        g.fillText(label, x(Math.max(from, n.gradedFrom)), y(n.level) - 10);
        if (n.scoop) {
          g.fillStyle = SCOOP_COLOR;
          g.fillText(n.scoop.direction === 'up' ? SCOOP_UP : SCOOP_DOWN, x(Math.max(from, n.from)) - 10, y(n.level));
        }
        g.font = '10px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
      }
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [open, canvasRef, bufferRef, notesRef]);
}

const PitchMonitor = ({ open, onClose }) => {
  const monitor = usePitchMonitor({ open });
  const { ready, error, status, reading, notes, notesRef, bufferRef, target, setTarget, playTarget, clearNotes, resumeAudio } = monitor;
  const canvasRef = useRef(null);
  useGraph(canvasRef, open, bufferRef, notesRef, target);

  // Escape closes; other keys stay out of the practice shell's shortcuts
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  let main = '—';
  let detail = ready ? 'Listening… sing or hum a note.' : 'Opening the microphone…';
  if (error) detail = error;
  else if (reading) {
    if (reading.sung) {
      const base = target !== null ? target : Math.round(reading.midi);
      main = `${noteName(target !== null ? target : reading.midi)} ${signed(centsFrom(reading.midi, base))}¢`;
      detail = `${reading.hz.toFixed(1)} Hz · counted as singing`;
    } else if (reading.level < -55) {
      detail = 'Not counted: too quiet (or silence)';
    } else if (reading.clarity < CLEAR) {
      detail = 'Not counted: no clear pitch (a breath, a consonant or noise)';
    } else {
      detail = 'Not counted: too short or too quiet next to your singing';
    }
  }
  const level = reading ? reading.level : -90;
  const levelShare = Math.max(0, Math.min(1, (level + 60) / 50));
  const levelState = level > -8 ? 'loud' : level < -42 ? 'quiet' : 'ok';

  return (
    <div className="dps-monitor-backdrop" onMouseDown={onClose}>
      <div className="dps-monitor" role="dialog" aria-label="Pitch monitor" onMouseDown={(e) => e.stopPropagation()}>
        <header className="dps-monitor-head">
          <div>
            <h2>Pitch monitor</h2>
            <p>Sing or hum: this shows exactly what the grader hears, and how it would judge each note you hold.</p>
          </div>
          <button type="button" className="dps-monitor-close" onClick={onClose} aria-label="Close (Esc)" title="Close (Esc)">{'✕'}</button>
        </header>

        <div className="dps-monitor-controls">
          <label>
            Target
            <select value={target === null ? '' : String(target)} onChange={(e) => setTarget(e.target.value === '' ? null : Number(e.target.value))}>
              <option value="">Nearest note</option>
              {TARGETS.map(m => <option key={m} value={m}>{noteName(m)}</option>)}
            </select>
          </label>
          <button type="button" onClick={playTarget} disabled={target === null}>{'▶'} Play target</button>
          <div className={`dps-monitor-level ${levelState}`} title={`Mic level ${Math.round(level)} dB`}>
            <span>Mic</span>
            <div><i style={{ width: `${Math.round(levelShare * 100)}%` }} /></div>
            <small>{levelState === 'quiet' ? 'quiet' : levelState === 'loud' ? 'very loud' : 'good'}</small>
          </div>
        </div>

        <div className={`dps-monitor-readout ${reading && reading.sung ? 'sung' : ''}`}>
          <strong>{main}</strong>
          <span>{detail}</span>
        </div>

        <div className={`dps-monitor-status ${error || (status && (!status.running || !status.arriving || status.processing?.length)) ? 'problem' : ''}`}>
          {error ? (
            <span>{error}. Check that this site may use the microphone, and that the right mic is chosen in your system's sound settings.</span>
          ) : status ? (
            <>
              <span>
                Mic: {status.label || 'default microphone'} {'·'} {Math.round(status.sampleRate / 100) / 10} kHz {'·'}{' '}
                {!status.running ? 'audio paused by the browser' : status.arriving ? 'sound arriving' : 'no sound arriving from the microphone'}
              </span>
              <span title={PROCESSING_TIP}>
                {status.processing === null
                  ? 'browser voice processing: not reported'
                  : status.processing.length
                    ? `browser voice processing ON (${status.processing.join(', ')}): it can chop held notes and make Windows turn other sounds down`
                    : 'browser voice processing off ✓'}
              </span>
              {!status.running && <button type="button" onClick={resumeAudio}>Start audio</button>}
            </>
          ) : (
            <span>Waiting for the microphone{'…'} (allow it if the browser asks)</span>
          )}
        </div>

        <canvas ref={canvasRef} className="dps-monitor-graph" />

        <div className="dps-monitor-key">
          <span><i style={{ background: '#2563eb' }} />counted as singing</span>
          <span><i style={{ background: '#94a3b8' }} />heard, not counted</span>
          <span><i className="bar" style={{ background: GRADE_STYLES.good.color }} />graded part of a held note</span>
          {target !== null && <span><i className="band" />{'±'}25{'¢'} of the target</span>}
        </div>

        <div className="dps-monitor-notes">
          <div className="dps-monitor-notes-head">
            <span>Held notes, as the grader would judge them</span>
            {notes.length > 0 && <button type="button" onClick={clearNotes}>Clear</button>}
          </div>
          {notes.length === 0 ? (
            <p>Hold a note for a moment, then stop or move to another note.</p>
          ) : (
            <ul>
              {[...notes].reverse().map(n => (
                <li key={n.from} style={{ color: GRADE_STYLES[n.grade].color }}>{describeNote(n)}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default PitchMonitor;
